import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { 
  Package, 
  Save, 
  X, 
  Plus, 
  Minus, 
  DollarSign, 
  Users, 
  Calendar,
  CheckCircle,
  Clock,
  GitBranch,
  AlertCircle,
  Eye,
  Edit,
  History
} from 'lucide-react';
import { Badge } from '../ui/Table';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export const PlanEditor = ({ planId, onClose, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [plan, setPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    key: '',
    display_order: 1,
    is_most_popular: false,
    is_public: true,
    pricing: {
      monthly: 0,
      annual: 0
    },
    included_allocations: {
      contacts_limit: 100,
      voice_minutes: 1000,
      sms_count: 500,
      storage_gb: 5,
      team_members: 1
    },
    features: [],
    tariffs: {
      voice_minutes: { rate: 0.03, currency: 'USD' },
      sms_count: { rate: 0.02, currency: 'USD' }
    }
  });
  const [version, setVersion] = useState(1);
  const [justification, setJustification] = useState('');
  const [impactAssessment, setImpactAssessment] = useState('');

  useEffect(() => {
    if (planId) {
      fetchPlanData();
    }
  }, [planId]);

  const fetchPlanData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/monetization/plans`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        const planData = data.plans?.find(p => p.id === planId);
        
        if (planData) {
          setPlan(planData);
          // Populate form with current plan data
          const currentVersion = planData.current_version;
          setFormData({
            name: planData.name,
            description: planData.description,
            key: planData.key,
            display_order: planData.display_order,
            is_most_popular: planData.is_most_popular,
            is_public: planData.is_public,
            pricing: currentVersion?.pricing || { monthly: 0, annual: 0 },
            included_allocations: currentVersion?.included_allocations || {
              contacts_limit: 100,
              voice_minutes: 1000,
              sms_count: 500,
              storage_gb: 5,
              team_members: 1
            },
            features: currentVersion?.features || [],
            tariffs: currentVersion?.tariffs || {
              voice_minutes: { rate: 0.03, currency: 'USD' },
              sms_count: { rate: 0.02, currency: 'USD' }
            }
          });
          setVersion(currentVersion?.version || 1);
        }
      } else {
        setError('Failed to load plan data');
      }
    } catch (err) {
      console.error('Error fetching plan:', err);
      setError('Failed to load plan information');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedInputChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
  };

  const handleFeatureAdd = (feature) => {
    if (!formData.features.includes(feature)) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, feature]
      }));
    }
  };

  const handleFeatureRemove = (feature) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter(f => f !== feature)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!justification.trim()) {
      setError('Justification is required for plan changes');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload = {
        action: planId ? 'update' : 'create',
        plan_id: planId,
        changes: formData,
        justification: justification.trim(),
        impact_assessment: {
          user_provided: impactAssessment.trim()
        }
      };

      const response = await fetch(`${BACKEND_URL}/api/admin/monetization/plan-changes`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();
        onSave && onSave(result);
        onClose && onClose();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to submit plan changes');
      }
    } catch (err) {
      console.error('Error submitting plan changes:', err);
      setError('Failed to submit plan changes');
    } finally {
      setLoading(false);
    }
  };

  const availableFeatures = [
    'ai_insights',
    'gift_planning', 
    'social_radar',
    'advanced_analytics',
    'priority_support',
    'white_label'
  ];

  if (loading && !plan) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-4xl w-full max-h-screen overflow-y-auto dark:bg-gray-900">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-300">Loading plan data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-6xl w-full max-h-screen overflow-y-auto dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Package className="w-6 h-6 text-indigo-600" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {planId ? 'Edit Plan' : 'Create New Plan'}
              </h2>
              <p className="text-gray-600 dark:text-gray-300">
                {planId ? `Version ${version + 1} (requires approval)` : 'Create a new subscription plan'}
              </p>
            </div>
          </div>
          <Button onClick={onClose} variant="outline" size="sm">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-900/10 dark:border-red-800">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
              <p className="text-red-800 dark:text-red-200">{error}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Basic Information</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Plan Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Plan Key
                </label>
                <input
                  type="text"
                  value={formData.key}
                  onChange={(e) => handleInputChange('key', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={formData.display_order}
                    onChange={(e) => handleInputChange('display_order', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                    min="1"
                    required
                  />
                </div>
                
                <div className="space-y-2 pt-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_most_popular}
                      onChange={(e) => handleInputChange('is_most_popular', e.target.checked)}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Most Popular</span>
                  </label>
                  
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_public}
                      onChange={(e) => handleInputChange('is_public', e.target.checked)}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Public</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Pricing */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Pricing</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Monthly Price ($)
                  </label>
                  <input
                    type="number"
                    value={formData.pricing.monthly}
                    onChange={(e) => handleNestedInputChange('pricing', 'monthly', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                    min="0"
                    step="0.01"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Annual Price ($)
                  </label>
                  <input
                    type="number"
                    value={formData.pricing.annual}
                    onChange={(e) => handleNestedInputChange('pricing', 'annual', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              {formData.pricing.annual > 0 && formData.pricing.monthly > 0 && (
                <div className="p-3 bg-green-50 rounded-lg dark:bg-green-900/10">
                  <p className="text-sm text-green-700 dark:text-green-300">
                    Annual savings: ${(formData.pricing.monthly * 12 - formData.pricing.annual).toFixed(2)} 
                    ({(((formData.pricing.monthly * 12 - formData.pricing.annual) / (formData.pricing.monthly * 12)) * 100).toFixed(1)}%)
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Allocations */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Included Allocations</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Contacts Limit
                </label>
                <input
                  type="number"
                  value={formData.included_allocations.contacts_limit}
                  onChange={(e) => handleNestedInputChange('included_allocations', 'contacts_limit', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                  min="0"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Voice Minutes
                </label>
                <input
                  type="number"
                  value={formData.included_allocations.voice_minutes}
                  onChange={(e) => handleNestedInputChange('included_allocations', 'voice_minutes', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                  min="0"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  SMS Count
                </label>
                <input
                  type="number"
                  value={formData.included_allocations.sms_count}
                  onChange={(e) => handleNestedInputChange('included_allocations', 'sms_count', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                  min="0"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Storage (GB)
                </label>
                <input
                  type="number"
                  value={formData.included_allocations.storage_gb}
                  onChange={(e) => handleNestedInputChange('included_allocations', 'storage_gb', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                  min="0"
                  step="0.1"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Team Members
                </label>
                <input
                  type="number"
                  value={formData.included_allocations.team_members}
                  onChange={(e) => handleNestedInputChange('included_allocations', 'team_members', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                  min="1"
                />
              </div>
            </div>
          </div>

          {/* Features */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Features</h3>
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {formData.features.map(feature => (
                  <Badge key={feature} variant="success" className="flex items-center gap-1">
                    {feature}
                    <button
                      type="button"
                      onClick={() => handleFeatureRemove(feature)}
                      className="ml-1 hover:bg-red-100 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              
              <div className="flex flex-wrap gap-2">
                {availableFeatures.filter(f => !formData.features.includes(f)).map(feature => (
                  <button
                    key={feature}
                    type="button"
                    onClick={() => handleFeatureAdd(feature)}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm border border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800 dark:text-gray-300"
                  >
                    <Plus className="w-3 h-3" />
                    {feature}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Approval Requirements */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Approval Requirements</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Justification <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  placeholder="Explain why this change is needed..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Impact Assessment
                </label>
                <textarea
                  value={impactAssessment}
                  onChange={(e) => setImpactAssessment(e.target.value)}
                  placeholder="Describe the expected impact on users and business..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                />
              </div>

              <div className="p-4 bg-yellow-50 rounded-lg dark:bg-yellow-900/10">
                <div className="flex items-start">
                  <Clock className="w-5 h-5 text-yellow-600 mt-0.5 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                      Maker-Checker Approval Required
                    </p>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                      This change requires approval from 2 super administrators before it can be implemented.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-6 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Submit for Approval
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlanEditor;